'use client'

import { ChevronDown } from 'lucide-react'
import useToggleState from "@lib/hooks/use-toggle-state"

/**
 * Variant determines the visual expand/collapse behavior:
 *
 * - "inline": Extended text appears as a <span> inside the same paragraph.
 * - "block": Extended text appears in a separate animated container below
 *   the main text, with a ChevronDown icon on the toggle button.
 * - "fade": Main text is clipped to a max height with a gradient fade
 *   overlay when collapsed. No separate extended text — the full text
 *   is revealed on expand.
 */
type ExpandableTextVariant = 'inline' | 'block' | 'fade'

interface ExpandableTextProps {
  /** The primary text always visible */
  text: string
  /** Additional text revealed on expand (used by "inline" and "block" variants) */
  extendedText?: string
  /** Visual variant — determines how expanded content is shown */
  variant?: ExpandableTextVariant
  /** CSS class for the collapsed max-height container (fade variant only). Default: "max-h-[120px]" */
  maxHeightCollapsed?: string
  /** CSS class for the fade gradient overlay (fade variant only). Default: gradient from gray-50 */
  fadeGradientClassName?: string
  /** Label for the expand button. Default: "Read more" (or "Read More" for fade) */
  expandLabel?: string
  /** Label for the collapse button. Default: "Read less" (or "Read Less" for fade) */
  collapseLabel?: string
  /** CSS class applied to the text paragraphs */
  textClassName?: string
  /** CSS class applied to the toggle button */
  buttonClassName?: string
  /** Whether to always show the toggle button (fade variant). Default: true for fade, conditional for inline/block */
  alwaysShowButton?: boolean
}

/**
 * Reusable expandable text component that supports three visual patterns:
 *
 * 1. **inline** — appends extended text as a span within the same paragraph
 * 2. **block** — shows extended text in an animated container with chevron icon
 * 3. **fade** — clips text with a gradient fade overlay, reveals full text on expand
 *
 * Uses the shared `useExpandable` hook for toggle state.
 */
export default function ExpandableText({
  text,
  extendedText,
  variant = 'inline',
  maxHeightCollapsed = 'max-h-[120px]',
  fadeGradientClassName = 'bg-gradient-to-t from-gray-50 to-transparent',
  expandLabel,
  collapseLabel,
  textClassName = 'text-sm md:text-base text-brand-body',
  buttonClassName,
  alwaysShowButton,
}: ExpandableTextProps) {
  const { state: isExpanded, toggle } = useToggleState()

  // Determine whether to show the toggle button
  const hasExtendedContent = variant === 'fade' ? true : !!extendedText
  const showButton = alwaysShowButton ?? hasExtendedContent

  // Default labels differ by variant to match existing behavior
  const resolvedExpandLabel = expandLabel ?? (variant === 'fade' ? 'Read More' : 'Read more')
  const resolvedCollapseLabel = collapseLabel ?? (variant === 'fade' ? 'Read Less' : 'Read less')

  // Default button styles differ by variant to match existing designs
  const resolvedButtonClassName =
    buttonClassName ??
    (variant === 'block'
      ? 'mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand-navy hover:text-brand-hero-navy transition-colors outline-none focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:ring-offset-2 rounded'
      : variant === 'fade'
        ? 'mt-2 text-sm font-medium text-brand-primary-blue hover:underline outline-none focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:ring-offset-2 rounded'
        : 'mt-4 text-sm text-brand-primary-blue underline hover:text-brand-primary-blue/80 transition-colors outline-none rounded focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:ring-offset-2')

  if (variant === 'fade') {
    return (
      <>
        <div className={`relative ${isExpanded ? '' : maxHeightCollapsed} overflow-hidden`}>
          <div className={`${textClassName} leading-relaxed space-y-4`}>
            <p>{text}</p>
          </div>
          {/* Fade overlay — only shown when collapsed */}
          {!isExpanded && (
            <div
              className={`absolute bottom-0 left-0 right-0 h-12 ${fadeGradientClassName} pointer-events-none`}
            />
          )}
        </div>
        {showButton && (
          <button
            type="button"
            onClick={toggle}
            className={resolvedButtonClassName}
          >
            {isExpanded ? resolvedCollapseLabel : resolvedExpandLabel}
          </button>
        )}
      </>
    )
  }

  if (variant === 'block') {
    return (
      <>
        <p className={`${textClassName} leading-relaxed`}>{text}</p>

        {extendedText && (
          <div
            className={`overflow-hidden transition-all duration-300 ${
              isExpanded ? 'max-h-[1000px] mt-4' : 'max-h-0'
            }`}
          >
            <p className={`${textClassName} leading-relaxed`}>{extendedText}</p>
          </div>
        )}

        {showButton && (
          <button
            onClick={toggle}
            className={resolvedButtonClassName}
            aria-expanded={isExpanded}
          >
            {isExpanded ? resolvedCollapseLabel : resolvedExpandLabel}
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-200 ${
                isExpanded ? 'rotate-180' : ''
              }`}
              aria-hidden="true"
            />
          </button>
        )}
      </>
    )
  }

  // variant === 'inline'
  return (
    <>
      <p className={textClassName}>
        {text}
        {isExpanded && extendedText && <span> {extendedText}</span>}
      </p>

      {showButton && (
        <button
          onClick={toggle}
          className={resolvedButtonClassName}
          aria-expanded={isExpanded}
        >
          {isExpanded ? resolvedCollapseLabel : resolvedExpandLabel}
        </button>
      )}
    </>
  )
}
